import React, { useEffect, useMemo, useRef, useState, useContext } from "react";
import {
    Box,
    Typography,
    Divider,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    makeStyles,
    Paper,
    Grid
} from "@material-ui/core";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import SaveIcon from "@material-ui/icons/Save";
import Alert from 'views/Components/Alert';
import MDEditor from "@uiw/react-md-editor";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/survey-core.css";
import NotificationContext from "views/Components/Context.js";
import Button from "components/CustomButtons/Button.js";
import { saveNewJSONDesignInJSONProjectAPI } from "views/APIs/APIUtility";
import '../qu-brand.css';

const useStyles = makeStyles((theme) => ({
    container: { padding: theme.spacing(3) },
    topBar: { display: "flex", justifyContent: "space-between", alignItems: "center" },
    meta: { display: "flex", flexDirection: "column" },
    controls: { display: "flex", alignItems: "center", gap: theme.spacing(2) },
    twoCol: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gridGap: theme.spacing(2),
        [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" }
    },
    editorPaper: { padding: theme.spacing(2), height: 600, overflow: "hidden" },
    previewPaper: { padding: theme.spacing(2), height: 600, overflow: "auto" },
    buttonRow: { marginTop: theme.spacing(2), textAlign: "right" },
}));

const ReviewJson = ({ formId, jsonDataArray, onSaveVersion, onClickNext }) => {
    const classes = useStyles();
    const { showNotification } = useContext(NotificationContext);

    // Local copy of versions so we can append on save
    const [versions, setVersions] = useState(() =>
        [...(jsonDataArray || [])].sort((a, b) => a.version - b.version)
    );

    const latestVersion = useMemo(() => (versions.length ? versions[versions.length - 1].version : 1), [versions]);

    const [selectedVersion, setSelectedVersion] = useState(latestVersion);
    const currentJsonObj = useMemo(() => {
        const match = versions.find((v) => v.version === selectedVersion);
        return match?.json || {};
    }, [versions, selectedVersion]);

    const [editorValue, setEditorValue] = useState(() => JSON.stringify(currentJsonObj, null, 2));
    const [parseError, setParseError] = useState("");
    const [saving, setSaving] = useState(false);

    // Keep editor text in sync when switching versions
    useEffect(() => {
        setEditorValue(JSON.stringify(currentJsonObj, null, 2));
        setParseError("");
    }, [currentJsonObj]);

    // Attempt to parse the editor JSON (memoized)
    const parsedSurveyJson = useMemo(() => {
        try {
            if (!editorValue?.trim()) return {};
            const obj = JSON.parse(editorValue);
            setParseError("");
            return obj;
        } catch (e) {
            setParseError(e.message);
            return null; // invalid JSON
        }
    }, [editorValue]);

    const surveyModel = useMemo(() => {
        if (!parsedSurveyJson || typeof parsedSurveyJson !== "object") return null;
        try {
            return new Model(parsedSurveyJson);
        } catch (e) {
            // SurveyJS config error (schema-level), surface succinctly
            setParseError(`SurveyJS config error: ${e.message}`);
            return null;
        }
    }, [parsedSurveyJson]);

    const handleChangeVersion = (v) => {
        setSelectedVersion(v);
    };

    const convertUtcToLocalWithOffset = (utcTimeString, offsetHours = -5) => {
        const date = new Date(utcTimeString); // Convert UTC string to Date object

        // Add the offset hours
        date.setHours(date.getHours() + offsetHours);

        // Format to the user's local timezone
        return date.toLocaleString();
    };

    const handleSaveNewVersion = async () => {
        // Ensure valid JSON before attempting save
        if (parseError || parsedSurveyJson === null) {
            showNotification("Fix JSON errors before saving.", "warning");
            return;
        }

        const nextVersion = (versions.length ? Math.max(...versions.map((x) => x.version)) : 0) + 1;

        setSaving(true);
        try {
            // If a save hook is provided, use it; otherwise simulate success
            const ok = onSaveVersion ? await onSaveVersion(parsedSurveyJson) : true;

            const newEntry = { version: nextVersion, json: parsedSurveyJson, created_at: new Date() };
            setVersions((prev) => [...prev, newEntry].sort((a, b) => a.version - b.version));
            setSelectedVersion(nextVersion);
            showNotification("New version saved.", "success");
        } catch (err) {
            console.error(err);
            showNotification(err.message || "Failed to save version", "danger");
        } finally {
            setSaving(false);
        }
    };
    // Derive title/description for the header (display only; editable via JSON)
    const headerTitle = "Review JSON";
    const headerDesc = "Edit the JSON on the left and preview the form on the right.";

    const handleDownload = () => {
        const blob = new Blob([JSON.stringify(parsedSurveyJson)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "survey.json";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <Box >
            {/* Top bar (mimics NotebookReview) */}
            <Box className={classes.topBar}>
                <Box className={classes.meta}>
                    <Typography variant="h5">{headerTitle}</Typography>
                    <Typography variant="body1">{headerDesc}</Typography>
                </Box>

                <Box className={classes.controls} mr={2}>
                    <FormControl style={{ minWidth: "180px", float: "right", marginRight: "10px" }}>
                        <InputLabel id="version-select-label">Select Version</InputLabel>
                        <Select
                            labelId="version-select-label"
                            value={selectedVersion}
                            renderValue={(selected) => {
                                // Display only the version number when the dropdown is not open
                                const selectedOption = versions.find(
                                    (option) => option.version === selected
                                );
                                return selectedOption
                                    ? `Version ${selectedOption.version}`
                                    : "";
                            }}
                            onChange={(e) => handleChangeVersion(e.target.value)}
                            label="Version"
                            style={{ minWidth: 140 }}
                        >
                            {versions.map((option, index) => (
                                <MenuItem key={index} value={option.version}>
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <Typography variant="body1">
                                                {" "}
                                                {"Version: " + option.version}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography
                                                variant="caption"
                                                style={{ fontStyle: "italic" }}
                                            >
                                                {"Last Updated: " +
                                                    convertUtcToLocalWithOffset(option.created_at)}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* add a dwonload button */}
                    <Button
                        color="quCreateColor"
                        variant="contained"
                        onClick={handleDownload}
                        disabled={saving || parsedSurveyJson === null}
                    >
                        Download JSON
                    </Button>

                </Box>
            </Box>

            <Divider style={{ marginTop: 16, marginBottom: 16 }} />



            {/* Two column view */}
            <Box className={classes.twoCol}>
                {/* Left: JSON editor */}
                <div data-color-mode="light">
                    <MDEditor
                        height="520px"
                        defaultLanguage="json"
                        value={editorValue}
                        onChange={(val) => setEditorValue(val ?? "")}
                        options={{
                            minimap: { enabled: false },
                            fontSize: 13,
                            scrollBeyondLastLine: false,
                            wordWrap: "on",
                            automaticLayout: true,
                            showLineNumbers: true,
                        }}
                        preview="edit"
                        showLineNumbers={true}
                    />
                </div>

                <div style={{ height: "520px" }}>
                    {/* Right: SurveyJS render */}
                    {surveyModel ? (
                        <div className="qu-theme" style={{ height: "520px" }}>
                            <Survey model={surveyModel} />
                        </div>
                    ) : parseError ? (
                        <Typography variant="body2" color="error">
                            {parseError}
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="textSecondary">
                            Provide valid SurveyJS JSON to preview the survey.
                        </Typography>
                    )}
                </div>
            </Box>

            <Divider style={{ marginTop: 16, marginBottom: 16 }} />


            <Box className={classes.buttonRow}>
                <Button
                    color="quCreateColor"
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                    onClick={handleSaveNewVersion}
                    disabled={saving || parsedSurveyJson === null}
                >
                    Save new version
                </Button>
                <Button color="quCreateColor" endIcon={<NavigateNextIcon />} onClick={onClickNext}>
                    Next
                </Button>
            </Box>
        </Box>
    );
};

export default ReviewJson;
